import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import H3 from "../atoms/typography/H3"
import Subtitle from "../atoms/typography/Subtitle"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import InvitationSentEmail from "../molecules/InvitationSentEmail"
import TextInput from "../molecules/TextInput"
import * as Sentry from "@sentry/react"
import { useMyOrgMembers } from "../../hooks/useMyOrgMembers"
import { formatMembers } from "../../utils/formatters"
import { checkEmailValid } from "../../utils/helpers"
import MemberProfileOverview from "../molecules/MemberProfileOverview"
import { PersonAddIcon } from "../atoms/icons/PersonAddIcon"
import H4 from "../atoms/typography/H4"
import Checkbox from "../atoms/inputs/Checkbox"
import { useMyOrgGroups } from "../../hooks/useMyOrgGroups"
import SecondaryButton from "../atoms/buttons/SecondaryButton"
import { useCreateOrganizationGroup } from "../../hooks/useCreateOrganizationGroup"
import SelectRoleDropdown from "../atoms/inputs/SelectRoleDropdown"
import AddMemberCard from "../molecules/AddMemberCard"
import { theme } from "../theme"
import { contactInformation } from "../../utils/design-helpers"
import { TerminalType } from "../../utils/models"
import { CheckCircleIcon } from "../atoms/icons/CheckCircleIcon"
import { captureExceptionWithContext } from "../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface Props {
  onClickCancelEditing: () => void
}

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`
const AddMemberContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: ${(props) => props.theme.spacings.medium};

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`

const IconContainer = styled.div`
  cursor: pointer;

  :hover {
    filter: brightness(60%);
  }
`

const CreateGroupOrganism: React.FunctionComponent<Props> = ({ onClickCancelEditing }) => {
  const { myOrganizationMembers } = useMyOrgMembers()
  const { myOrganizationGroups } = useMyOrgGroups()

  const [groupName, setGroupName] = React.useState("")
  const formattedMembers = formatMembers(myOrganizationMembers?.users ?? [])
  const [invitedMembers, setInvitedMembers] = React.useState<Array<{ email: string; role: string }>>([])
  const [invitedTerminals, setInvitedTerminals] = React.useState<Array<{ id: number }>>([])
  const [emailIvites, setEmailInvites] = React.useState<Array<string>>([])
  const [emailValue, setEmailValue] = React.useState<string>("")

  const [checkAllTerminals, setCheckAllTerminals] = React.useState(false)
  const [selectedValue, setSelectedValue] = React.useState<string>("Viewer")

  const [showSuccessText, setShowSuccessText] = React.useState(false)

  const [errors, setErrors] = React.useState<Array<string>>([])

  const myTerminals = myOrganizationGroups ? myOrganizationGroups.groups[0].terminals : []

  const onClickAddAllTerminals = () => {
    if (checkAllTerminals) {
      setCheckAllTerminals(false)
    } else {
      let list: Array<{ id: number }> = []
      myTerminals?.map((t: any) => list.push({ id: parseInt(t.id) }))
      setInvitedTerminals(list)
      setCheckAllTerminals(true)
    }
  }

  const onClickAddSuggestedMember = (role: string, email?: string) => {
    if (!email || email === "error") {
      Sentry.captureMessage("Error, user missing email on invite")
    } else {
      setInvitedMembers([...invitedMembers, { email: email, role: role }])
      console.log(`Adding ${email} as ${role}`)
    }
  }

  const onClickRemoveAddedMember = (email: string) => {
    console.log("removing ", email)
    const filteredList = invitedMembers.filter((x) => x.email !== email)
    setInvitedMembers(filteredList)
  }

  const onClickAddSuggestedTerminal = (id: string) => {
    setInvitedTerminals([...invitedTerminals, { id: parseInt(id) }])
  }

  const onClickSendInvite = () => {
    if (checkEmailValid(emailValue ?? "")) {
      const email = emailValue
      setInvitedMembers([...invitedMembers, { email: email, role: selectedValue }])
      setEmailInvites([...emailIvites, email])
      setEmailValue("")
    } else {
      alert("Invalid email format")
    }
  }

  const onSelectRole = (role: string) => {
    setSelectedValue(role)
  }

  const onCompletedCreateGroup = (data: any) => {
    if (data.createGroup?.errors?.length > 0) {
      setErrors(data.createGroup.errors)
      setTimeout(() => {
        setErrors([])
      }, 3000)
      return
    }
    setGroupName("")
    setEmailInvites([])
    setInvitedTerminals([])
    setInvitedMembers([])
    setShowSuccessText(true)
    setTimeout(() => {
      setShowSuccessText(false)
      setErrors([])
      onClickCancelEditing()
    }, 2500)
  }
  const onErrorCreateGroup = (error: ApolloError) => {
    captureExceptionWithContext(error, "error creating group")
    setErrors([`Error when creating this group. For questions, contact ${contactInformation.email.value}`])
    setTimeout(() => {
      setErrors([])
    }, 3000)
  }
  const { createOrgGroup, data } = useCreateOrganizationGroup({
    onCompleted: (data: any) => onCompletedCreateGroup(data),
    onError: onErrorCreateGroup,
  })

  const onClickCreateGroup = async () => {
    console.log("Group name: ", groupName)
    console.log("Members: ", invitedMembers)
    console.log("Terminals: ", invitedTerminals)

    if (groupName.length > 0) {
      try {
        await createOrgGroup({
          variables: {
            name: groupName,
            organizationId: parseInt(myOrganizationGroups.id),
            terminalIds: invitedTerminals,
            users: invitedMembers,
          },
        })
      } catch (error) {
        captureExceptionWithContext(error, "error creating group")
      }
    }
  }

  const checkMemberIsAdded = (email: string) => {
    const res = invitedMembers.find((e) => e.email === email)
    return res ? true : false
  }
  const checkTerminalIsAdded = (id: string) => {
    const res = invitedTerminals.find((e) => e.id === parseInt(id))
    return res ? true : false
  }

  return (
    <ContentContainer>
      <HorizontalSpacer spacing={2} />
      <Row justify="center">
        <Col md={6}>
          <TextInput placeholder="Group Name" onChange={(e) => setGroupName(e.target.value)} />
        </Col>
      </Row>
      <HorizontalSpacer spacing={3} />
      <Subtitle>Invite members:</Subtitle>
      <HorizontalSpacer spacing={2} />
      <div style={{ display: "flex", alignItems: "baseline" }}>
        <Col md={4}>
          <TextInput
            textFieldTitle="Invite through email"
            placeholder="Email address of the person you want to invite"
            onChange={(e) => setEmailValue(e.target.value)}
          />
        </Col>
        <Spacer spacing={2} />
        <SecondaryButton title="Send invite" onClick={onClickSendInvite} disabled={!checkEmailValid(emailValue)} />
      </div>
      <Spacer spacing={2} />
      <HorizontalSpacer spacing={1} />

      <SelectRoleDropdown title="Select group role: " onChange={onSelectRole} defaultValue={selectedValue} />
      <HorizontalSpacer spacing={2} />
      {emailIvites?.map((i: string) => (
        <div key={i} style={{ padding: 4 }}>
          <InvitationSentEmail email={i} />
        </div>
      ))}
      <HorizontalSpacer spacing={3} />
      <H3>Suggested members:</H3>
      <HorizontalSpacer spacing={1} />
      <Row>
        {formattedMembers?.map((member) => {
          return (
            <Col md={3} key={member.member.id}>
              <AddMemberCard
                member={member}
                onClickAdd={onClickAddSuggestedMember}
                isAdded={checkMemberIsAdded(member.member.email)}
                onClickRemoveAddedMember={onClickRemoveAddedMember}
              />
            </Col>
          )
        })}
      </Row>
      <HorizontalSpacer spacing={3} />
      <H4>Add terminals:</H4>
      <HorizontalSpacer spacing={2} />
      <div style={{ display: "flex", alignItems: "center" }}>
        <Checkbox checked={checkAllTerminals} onChange={onClickAddAllTerminals} />
        <Spacer spacing={1} />
        <H3>Let group access all terminals</H3>
      </div>
      <HorizontalSpacer spacing={3} />
      {!checkAllTerminals && (
        <>
          <H3>Suggested terminals:</H3>
          <HorizontalSpacer spacing={1} />

          <Row>
            {myTerminals?.map((t: TerminalType) => {
              return (
                <Col md={3} key={t?.serialNumber}>
                  <AddMemberContainer>
                    <MemberProfileOverview
                      imgSrc={t?.imageUrl ?? ""}
                      name={t?.nickname ?? ""}
                      role={t?.serialNumber ?? ""}
                    />
                    <IconContainer onClick={() => onClickAddSuggestedTerminal(t.id.toString())}>
                      {checkTerminalIsAdded(t.id.toString()) ? <CheckCircleIcon /> : <PersonAddIcon />}
                    </IconContainer>
                  </AddMemberContainer>
                </Col>
              )
            })}
          </Row>
        </>
      )}
      <HorizontalSpacer spacing={5} />
      <Row justify="center">
        <SecondaryButton title="Create group" onClick={onClickCreateGroup} />
        <Spacer spacing={1} />
        <PrimaryButton title="Cancel" onClick={onClickCancelEditing} />
      </Row>
      {showSuccessText && <H3 color={theme.colors.greenDark}>Group successfully created</H3>}
      {errors.length > 0 && (
        <div>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            {errors.map((e, index) => (
              <H3 key={index} color={theme.colors.red}>
                {e}
              </H3>
            ))}
          </Row>
        </div>
      )}
      <HorizontalSpacer spacing={3} />
    </ContentContainer>
  )
}

export default CreateGroupOrganism
